<template>
  <div
    class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
  >
    <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
      <!-- Error Message -->
      <div class="flex justify-center mb-4">
        <div
          v-if="errorMessage"
          role="alert"
          class="flex gap-2 w-full justify-center p-2.5 text-sm leading-5 text-center text-red-900 bg-red-50 rounded-lg max-md:flex-wrap"
          style="max-height: 80px; overflow-y: auto"
        >
          <div
            class="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full"
          >
            <img
              src="@/assets/shared/failure.svg"
              alt="Error Icon"
              class="w-4 h-4"
            />
          </div>
          <p>{{ errorMessage }}</p>
        </div>
      </div>

      <h3 class="text-lg font-semibold text-gray-900 mb-4">
        Delete "{{ layoutName }}"
      </h3>
      <p class="text-gray-600 mb-6">
        Are you sure you want to delete "{{ layoutName }}"? This action cannot
        be undone.
      </p>
      <div class="flex justify-end gap-3">
        <BaseButton
          type="button"
          class="text-gray-700 bg-gray-100 px-4 py-2 rounded-md font-inter text-sm font-medium"
          @click="handleCancel"
        >
          Cancel
        </BaseButton>
        <BaseButton
          type="button"
          class="bg-red-600 text-white px-4 py-2 rounded-md font-inter text-sm font-medium"
          :disabled="isDeleting"
          @click="handleDelete"
        >
          {{ isDeleting ? "Deleting..." : "Delete" }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { gql } from "@apollo/client/core";
import { useMutation } from "@vue/apollo-composable";
import BaseButton from "@/components/shared/BaseButton.vue";

const DELETE_DIRECTORY_LAYOUT = gql`
  mutation deleteDirectory($input: DeleteDirectoryInput!) {
    deleteDirectory(input: $input) {
      success
    }
  }
`;

export default defineComponent({
  name: "DeleteConfirmationModal",
  components: {
    BaseButton,
  },
  props: {
    layoutId: {
      type: String,
      required: true,
    },
    layoutName: {
      type: String,
      required: true,
    },
  },
  emits: ["deleted", "close"],
  setup(props, { emit }) {
    const errorMessage = ref("");
    const isDeleting = ref(false);

    const {
      mutate: deleteDirectory,
      onDone: deleteDirectoryDone,
      onError: deleteDirectoryError,
    } = useMutation(DELETE_DIRECTORY_LAYOUT);

    const handleDelete = () => {
      isDeleting.value = true;
      errorMessage.value = "";

      deleteDirectory({
        input: {
          id: props.layoutId,
        },
      });

      deleteDirectoryDone((response) => {
        isDeleting.value = false;
        if (response) {
          console.log("response", response);
          if (response?.errors && response?.errors.length > 0) {
            handleErrorMessage(response.errors[0].message);
          } else {
            if (response.data.deleteDirectory.success) {
              emit("deleted");
            } else {
              handleErrorMessage("Failed to delete layout");
            }
          }
        }
      });

      deleteDirectoryError((error) => {
        isDeleting.value = false;
        if (error) {
          handleErrorMessage(error.message);
        }
      });
    };

    const handleCancel = () => {
      errorMessage.value = "";
      emit("close");
    };

    const handleErrorMessage = (error: string) => {
      errorMessage.value = error;
      setTimeout(() => {
        errorMessage.value = "";
      }, 2200);
    };

    return {
      errorMessage,
      isDeleting,
      handleDelete,
      handleCancel,
    };
  },
});
</script>
